/**
 * Close any MUI menu or popover that is currently open.
 *
 * For a global shortcut that takes over the screen: without this, triggering the shortcut while
 * some other menu happens to be open leaves that menu floating behind whatever the shortcut
 * opened. MUI's ModalManager stacks them correctly and focus goes to the newest, so it is not
 * broken — just untidy.
 *
 * ## How, and why this way
 *
 * By clicking the popover's own backdrop, which is MUI's outside-click close path. Every MUI menu
 * already closes when you click away from it, and that click lands on an invisible backdrop
 * (`Popover` renders one with `invisible: true`) that is a direct child of the overlay root. So
 * this is simulating an interaction users perform constantly, not reaching into internals — and it
 * is exactly what already happens today if you click the Filters pill while the Sort menu is open.
 *
 * Deliberately NOT a synthetic `Escape`: MUI's Escape handler bails out unless the overlay
 * `isTopModal()`, and that check is evaluated against manager state that only updates after a
 * re-render — so dispatching Escape at several stacked popovers in one tick closes only the top
 * one. The backdrop-click path has no such check (`Modal/useModal.js:127-135`), so each popover
 * closes independently.
 *
 * ## Scope of the selector — read this before calling
 *
 * `.MuiPopover-root` covers `Popover`, `Menu` and `Select` (whose roots are `styled(Modal)` and so
 * carry both class names). It does NOT match real modals or drawers — `Modal` and `Drawer` roots
 * have no `MuiPopover-root` — and `Tooltip`/`Autocomplete` use Popper rather than Modal, so those
 * are untouched too.
 *
 * But "menus" is broader than it sounds. This also closes the **notification bell panel**
 * (`NotifBell.tsx`) and the **upload-status panel** (`UploadStatus.tsx`), which are plain MUI
 * `Popover`s and are global desktop chrome. Neither registers with `ModalStore`, so a caller's
 * `hasOpenModals` guard will not have held it back. So a user watching an upload batch who presses
 * a shortcut wired to this will lose that panel.
 *
 * That is deliberate but worth knowing: it matches the mouse path rather than diverging from it —
 * because those panels render a backdrop, clicking a header control while one is open already
 * closes the panel instead of hitting the control, so the pointer equivalent of this takes two
 * clicks. Do not call this from anything that should preserve ambient panels.
 *
 * `:not(.MuiModal-hidden)` filters out overlays that are mounted but not visible. MUI stamps that
 * class when `!open && exited`, which matters because `SwipeableDrawer` hardcodes `keepMounted` —
 * closed bottom sheets leave their root in the DOM forever.
 *
 * Callers should invoke this only on the path where they are actually about to take over, and only
 * once they know their OWN popover is not the one open — otherwise they will dismiss themselves.
 */
export function dismissOpenPopovers(): void {
  // Static NodeList, so closing one mid-iteration cannot disturb the walk.
  const popovers = document.querySelectorAll<HTMLElement>('.MuiPopover-root:not(.MuiModal-hidden)');

  popovers.forEach((popover) => {
    // Direct child only: `event.target !== event.currentTarget` makes MUI ignore clicks that
    // bubbled up from the content, so a nested backdrop would not close anything.
    popover.querySelector<HTMLElement>(':scope > .MuiBackdrop-root')?.click();
  });
}
